import type { Dashboard } from '@/dashboard/types';

import type { ChatDashboardArtifactsStore } from '../hooks/useChatDashboardArtifacts';
import {
  isArtifactContentEmpty,
  isArtifactDraftForDisplay,
  isArtifactPersisted,
} from '../hooks/useChatDashboardArtifacts';
import { toAgentDashboardSnapshot } from './agent-dashboard-snapshot';

/**
 * Snapshot-at-send for the agent preamble. Unsaved drafts use the live store;
 * persisted saved dashboards load the current row from the DB (external edits included).
 * Deleted row (fetch returns null) → omit preamble. Transient fetch errors → live snapshot.
 *
 * With no card open, the chat's only unsaved draft is sent instead: it is the base a
 * `preview_dashboard` panel patch applies to (ACT2-1078), and a settled reload closes every card.
 * Without it, the first edit after a reload would be refused and fall back to the full rewrite.
 */
export async function resolveDashboardDraftForAgent(
  store: ChatDashboardArtifactsStore,
  fetchDashboard: (slug: string) => Promise<Dashboard | null>,
): Promise<ReturnType<ChatDashboardArtifactsStore['snapshot']>> {
  const snapshot = store.snapshot();
  if (!snapshot) {
    const unsaved = store.artifactList.filter(
      (artifact) => isArtifactDraftForDisplay(artifact) && !isArtifactContentEmpty(artifact),
    );
    return unsaved.length === 1 ? toAgentDashboardSnapshot(unsaved[0]!) : undefined;
  }

  const artifact = store.activeArtifact;
  if (!artifact?.saved || artifact.externallyRemoved) {
    return snapshot;
  }

  if (!isArtifactPersisted(artifact)) {
    return snapshot;
  }

  let dashboard: Dashboard | null;
  try {
    dashboard = await fetchDashboard(artifact.saved.slug);
  } catch {
    return snapshot;
  }
  if (!dashboard) return undefined;

  return toAgentDashboardSnapshot(dashboard);
}
